import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RotateCcw, Eye, UserRound, Plus, Check } from 'lucide-react';
import {
  formatOrderSummaryText,
  orderItemsToCartLines,
} from '@/lib/assistant/assistantRepeatOrder';
import { getLocalSuggestedUpsellProducts } from '@/lib/assistant/assistantOrderCloseFlow';
import { cn } from '@/lib/utils';

/**
 * Modal al entrar al catálogo.
 * Pasos: detail → upsell (si hay sugerencias) → data
 */
export default function RepeatLastOrderDialog({
  open,
  onOpenChange,
  order,
  menuData,
  onBrowseMenu,
  onKeepData,
  onChangeData,
}) {
  const [step, setStep] = useState('detail');
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const suggestions = useMemo(() => {
    if (!order?.items?.length || !menuData) return [];
    return getLocalSuggestedUpsellProducts(menuData, orderItemsToCartLines(order), 3);
  }, [order, menuData]);

  useEffect(() => {
    if (!open) return;
    setStep('detail');
    setSelectedIds(new Set());
  }, [open]);

  const handleOpenChange = (next) => {
    if (!next) onBrowseMenu?.();
    onOpenChange?.(next);
  };

  const summary = order?.items?.length ? formatOrderSummaryText(order) : '';

  const selectedProducts = suggestions.filter((p) => selectedIds.has(String(p.id)));

  const goAfterRepeat = () => {
    if (suggestions.length > 0) {
      setSelectedIds(new Set(suggestions.map((p) => String(p.id))));
      setStep('upsell');
      return;
    }
    setSelectedIds(new Set());
    setStep('data');
  };

  const toggleSuggestion = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const finishWithMode = (mode) => {
    if (mode === 'same') onKeepData?.(order, selectedProducts);
    else onChangeData?.(order, selectedProducts);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm" data-testid="repeat-last-order-dialog">
        {step === 'detail' ? (
          <>
            <DialogHeader>
              <DialogTitle>Tu último pedido</DialogTitle>
              <DialogDescription>
                ¿Querés repetirlo o ver otras opciones?
              </DialogDescription>
            </DialogHeader>
            <pre className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-sm font-sans leading-relaxed text-foreground">
              {summary}
            </pre>
            <div className="flex flex-col gap-2 pt-1">
              <Button
                type="button"
                className="w-full"
                data-testid="repeat-last-order-yes"
                onClick={goAfterRepeat}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Repetir mi pedido
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                data-testid="repeat-last-order-browse"
                onClick={() => {
                  onBrowseMenu?.();
                  onOpenChange?.(false);
                }}
              >
                <Eye className="w-4 h-4 mr-2" />
                Ver otras opciones
              </Button>
            </div>
          </>
        ) : null}

        {step === 'upsell' ? (
          <>
            <DialogHeader>
              <DialogTitle>¿Sumás algo más?</DialogTitle>
              <DialogDescription>
                Sugerencias del local para acompañar tu pedido.
              </DialogDescription>
            </DialogHeader>
            <ul className="max-h-56 space-y-2 overflow-y-auto">
              {suggestions.map((product) => {
                const id = String(product.id);
                const selected = selectedIds.has(id);
                const price = Number(product.final_price ?? product.price) || 0;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => toggleSuggestion(id)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                        selected
                          ? 'border-primary bg-primary/5'
                          : 'border-border bg-background hover:bg-muted/50',
                      )}
                      data-testid={`repeat-upsell-${id}`}
                    >
                      <span
                        className={cn(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                          selected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-muted-foreground/40',
                        )}
                      >
                        {selected ? <Check className="h-3 w-3" /> : null}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-medium">{product.name}</span>
                      <span className="shrink-0 text-muted-foreground">
                        ${price.toLocaleString('es-CL')}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="flex flex-col gap-2 pt-1">
              <Button
                type="button"
                className="w-full"
                data-testid="repeat-last-order-upsell-continue"
                onClick={() => setStep('data')}
              >
                <Plus className="w-4 h-4 mr-2" />
                {selectedProducts.length > 0
                  ? `Continuar con ${selectedProducts.length} sugerencia${selectedProducts.length === 1 ? '' : 's'}`
                  : 'Continuar sin sugerencias'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setSelectedIds(new Set());
                  setStep('data');
                }}
              >
                No, gracias
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setStep('detail')}
              >
                Atrás
              </Button>
            </div>
          </>
        ) : null}

        {step === 'data' ? (
          <>
            <DialogHeader>
              <DialogTitle>Tus datos</DialogTitle>
              <DialogDescription>
                ¿Querés conservar los mismos datos (nombre, dirección y pago)?
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 pt-1">
              <Button
                type="button"
                className="w-full"
                data-testid="repeat-last-order-keep-data"
                onClick={() => finishWithMode('same')}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Sí, conservar
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                data-testid="repeat-last-order-change-data"
                onClick={() => finishWithMode('change')}
              >
                <UserRound className="w-4 h-4 mr-2" />
                Cambiar datos
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setStep(suggestions.length > 0 ? 'upsell' : 'detail')}
              >
                Atrás
              </Button>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
